import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Check, Lock, Zap, AlertTriangle, Archive } from 'lucide-react';
// Parametrized upsells — add future upsells here using the same pattern.
const UPSELLS = [{
  id: 'emergencia',
  Icon: AlertTriangle,
  title: 'Agregar Banco de Arte de Emergencia',
  increment: '+ $9.000',
  summaryLabel: '🚨 Incluye Banco de Arte de Emergencia',
  support: 'Ideal para suplencias o clases sin preparar — actividades listas sin necesitar formación en arte para coordinarlas.',
  includes: ['18 páginas para Primaria: actividades express y grupales', '18 páginas para Secundaria: consignas y proyectos exprés', 'Guía de manejo de grupo + checklist de preparación', 'Plantilla en blanco reutilizable']
}, {
  id: 'archivo',
  Icon: Archive,
  title: 'Agregar Recursos de Archivo — Secundaria',
  increment: '+ $9.000',
  summaryLabel: '📚 Incluye Recursos de Archivo — Secundaria',
  support: 'Material adicional de referencia y planificación para profundizar en Secundaria.',
  includes: ['Artistas Argentinos — Historia y Proyectos (30 pág.)', 'Kit de Planificación Anual de Arte (30 pág.)', '25 Proyectos para Ferias y Muestras de Arte (31 pág.)', 'Banco de Rúbricas Express — +200 criterios (32 pág.)']
}] as const;
// Each combination maps to the exact product info to show + checkout URL.
type ComboInfo = {
  name: string;
  price: string;
  comparePrice: string;
  button: string;
  url: string;
};
const COMBOS: Record<string, ComboInfo> = {
  '': {
    name: 'Sistema Profesional Arte Lista 2026',
    price: '$19.990',
    comparePrice: '$64.990',
    button: 'QUIERO MI SISTEMA ARTE LISTA 2026',
    url: 'https://edu.iterapro.shop/cart/44557485572189:1'
  },
  emergencia: {
    name: 'Sistema Profesional Arte Lista 2026 + Banco de Arte de Emergencia',
    price: '$28.990',
    comparePrice: '$97.990',
    button: 'QUIERO EL SISTEMA COMPLETO',
    url: 'https://edu.iterapro.shop/cart/44585410461789:1'
  },
  archivo: {
    name: 'Sistema Profesional Arte Lista 2026 + Recursos de Archivo Secundaria',
    price: '$28.990',
    comparePrice: '$97.990',
    button: 'QUIERO EL SISTEMA COMPLETO',
    url: 'https://edu.iterapro.shop/cart/44585446801501:1'
  },
  'emergencia+archivo': {
    name: 'Sistema Profesional Arte Lista 2026 — Edición Completa',
    price: '$37.990',
    comparePrice: '$129.990',
    button: 'QUIERO EL SISTEMA COMPLETO',
    url: 'https://edu.iterapro.shop/cart/44585470230621:1'
  }
};
export function Pricing() {
  const features = ['MegaPack Primaria — 6 tomos completos', 'MegaPack Secundaria — 6 tomos completos', '6 Bonos exclusivos incluidos', 'Acceso inmediato', 'Garantía de 7 días'];
  const [selected, setSelected] = useState<Record<string, boolean>>({
    emergencia: false,
    archivo: false
  });
  const toggle = (id: string, value: boolean) => setSelected(prev => ({
    ...prev,
    [id]: value
  }));
  // Build the combination key in the canonical order defined by UPSELLS.
  const activeUpsells = UPSELLS.filter(u => selected[u.id]);
  const comboKey = activeUpsells.map(u => u.id).join('+');
  const combo = COMBOS[comboKey] ?? COMBOS[''];
  const productName = combo.name;
  const currentPrice = combo.price;
  const comparePrice = combo.comparePrice;
  const buttonText = combo.button;
  const checkoutUrl = combo.url;
  const hasUpsell = activeUpsells.length > 0;
  return <section id="pricing" className="py-24 lg:py-32 bg-paper-alt relative overflow-hidden" data-id="element-188">
      <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10" data-id="element-189">
        <motion.div initial={{
        opacity: 0,
        y: 20
      }} whileInView={{
        opacity: 1,
        y: 0
      }} viewport={{
        once: true
      }} transition={{
        duration: 0.8,
        ease: 'easeOut'
      }} className="text-center mb-12" data-id="element-190">
          <div className="inline-flex items-center gap-2 px-5 py-2 rounded-full bg-terra-50 text-terra-600 font-medium text-xs uppercase tracking-widest mb-8 border border-terra-100" data-id="element-191">
            <Zap className="w-4 h-4 text-terra-500" data-id="element-192" />
            OFERTA LANZAMIENTO · 2X1
          </div>
          <h2 className="text-4xl md:text-5xl lg:text-6xl font-serif font-medium text-ink-800 tracking-tight leading-tight" data-id="element-193">
            Reservá tu acceso al{' '}
            <span className="text-brand-500" data-id="element-194">Sistema Arte Lista 2026</span>
          </h2>
        </motion.div>

        <motion.div initial={{
        opacity: 0,
        y: 20
      }} whileInView={{
        opacity: 1,
        y: 0
      }} viewport={{
        once: true
      }} transition={{
        duration: 0.8,
        ease: 'easeOut'
      }} className="bg-paper-card rounded-[24px] p-8 md:p-14 shadow-card border border-paper-border relative max-w-xl mx-auto" data-id="element-195">
          <div className="absolute top-0 left-1/2 -translate-x-1/2 -translate-y-1/2 bg-brand-500 text-white font-medium px-6 py-2 rounded-full text-xs uppercase tracking-widest shadow-sm whitespace-nowrap" data-id="element-196">
            Acceso completo
          </div>

          <div className="text-center mb-10 pt-4" data-id="element-197">
            <div className="text-ink-800 font-serif font-medium text-2xl mb-2 transition-all" data-id="element-198">
              {productName}
            </div>
            <p className="text-ink-600 text-sm mb-8 font-normal" data-id="element-199">
              MegaPack Primaria + MegaPack Secundaria + 6 Bonos + Garantía de 7
              días
            </p>

            <div className="mb-3 flex items-center justify-center gap-4" data-id="element-200">
              <span className="text-ink-400 line-through text-2xl font-medium" data-id="element-201">
                {comparePrice}
              </span>
              <span className="bg-accent-100 text-accent-700 font-medium text-xs px-3 py-1.5 rounded-full uppercase tracking-widest" data-id="element-202">
                -70%
              </span>
            </div>
            <div className="text-5xl sm:text-6xl md:text-7xl font-serif font-medium text-brand-500 mb-3 tracking-tight" data-id="element-203">
              {currentPrice}
            </div>
            <AnimatePresence data-id="element-204">
              {hasUpsell && <motion.p initial={{
              opacity: 0,
              height: 0
            }} animate={{
              opacity: 1,
              height: 'auto'
            }} exit={{
              opacity: 0,
              height: 0
            }} className="text-xs text-brand-600 font-medium mb-2" data-id="element-205">
                  Incluye Sistema Arte Lista 2026
                  {activeUpsells.map(u => u.id === 'emergencia' ? ' + Banco de Arte de Emergencia' : ' + Recursos de Archivo Secundaria')}
                </motion.p>}
            </AnimatePresence>
            <p className="text-sm text-ink-500 font-normal" data-id="element-206">
              Pago único · Sin suscripción
            </p>
          </div>

          <div className="space-y-4 mb-10 bg-paper-main rounded-[20px] p-8 border border-paper-border" data-id="element-207">
            {features.map((feature, i) => <div key={i} className="flex items-center gap-4" data-id="element-208">
                <div className="w-6 h-6 rounded-full bg-accent-100 text-brand-500 flex items-center justify-center shrink-0" data-id="element-209">
                  <Check className="w-3.5 h-3.5" strokeWidth={2} data-id="element-210" />
                </div>
                <span className="text-ink-800 font-medium text-base" data-id="element-211">
                  {feature}
                </span>
              </div>)}
          </div>

          {/* Optional upgrades */}
          <div className="space-y-4 mb-8" data-id="element-212">
            {UPSELLS.map(upsell => {
            const Icon = upsell.Icon;
            const isOn = selected[upsell.id];
            return <label key={upsell.id} htmlFor={`upsell-${upsell.id}`} className={`block cursor-pointer rounded-[1.75rem] border p-5 sm:p-6 transition-all ${isOn ? 'border-terra-200 bg-terra-50/60 shadow-sm' : 'border-paper-border bg-paper-main hover:border-terra-100'}`} data-id="element-213">
                  <div className="flex items-start gap-4" data-id="element-214">
                    <div className="relative flex items-center shrink-0 pt-0.5" data-id="element-215">
                      <input id={`upsell-${upsell.id}`} type="checkbox" checked={isOn} onChange={e => toggle(upsell.id, e.target.checked)} className="peer sr-only" data-id="element-216" />
                      <span className="w-6 h-6 rounded-md border-2 border-accent-200 bg-white flex items-center justify-center transition-all peer-checked:bg-brand-500 peer-checked:border-brand-500" aria-hidden="true" data-id="element-217">
                        {isOn && <Check className="w-4 h-4 text-white" strokeWidth={2.5} data-id="element-218" />}
                      </span>
                    </div>

                    <div className="flex-1 min-w-0 text-left" data-id="element-219">
                      <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-1.5 sm:gap-3" data-id="element-220">
                        <span className="font-medium text-ink-800 text-[15px] sm:text-base flex items-start gap-2" data-id="element-221">
                          <Icon className="w-4 h-4 mt-1 text-terra-500 shrink-0" strokeWidth={1.75} data-id="element-222" />
                          {upsell.title}
                        </span>
                        <span className="font-serif font-medium text-brand-500 text-lg shrink-0 pl-6 sm:pl-0" data-id="element-223">
                          {upsell.increment}
                        </span>
                      </div>

                      <ul className="mt-3 space-y-1.5" data-id="element-224">
                        {upsell.includes.map((item, i) => <li key={i} className="flex items-start gap-2 text-[13px] text-ink-600 font-normal leading-snug" data-id="element-225">
                            <span className="mt-1.5 w-1 h-1 rounded-full bg-terra-400 shrink-0" data-id="element-226" />
                            {item}
                          </li>)}
                      </ul>

                      <p className="mt-3 text-[12px] text-ink-500 font-normal italic leading-snug" data-id="element-227">
                        {upsell.support}
                      </p>
                    </div>
                  </div>
                </label>;
          })}
          </div>

          {/* Dynamic total summary */}
          <div className="text-center mb-4" data-id="element-228">
            <p className="font-serif font-medium text-ink-800 text-lg" data-id="element-229">
              Total hoy: <span className="text-brand-500" data-id="element-230">{currentPrice}</span>
            </p>
            <div className="mt-1 space-y-0.5" data-id="element-231">
              <AnimatePresence data-id="element-232">
                {activeUpsells.map(u => <motion.p key={u.id} initial={{
                opacity: 0,
                height: 0
              }} animate={{
                opacity: 1,
                height: 'auto'
              }} exit={{
                opacity: 0,
                height: 0
              }} className="text-xs text-ink-500 font-normal" data-id="element-233">
                    {u.summaryLabel}
                  </motion.p>)}
              </AnimatePresence>
            </div>
          </div>

          <a href={checkoutUrl} target="_blank" rel="noopener noreferrer" className="block w-full text-center bg-brand-500 hover:bg-brand-600 text-white font-medium py-5 px-6 rounded-2xl transition-colors duration-300 shadow-cta text-lg tracking-wide" data-id="element-234">
            {buttonText}
          </a>

          <p className="mt-4 text-center text-xs text-ink-500 font-normal" data-id="element-235">
            💻 Producto 100% digital (PDF) · Entrega automática por email · Sin
            envío físico
          </p>

          <p className="text-center text-xs text-ink-500 mt-6 inline-flex items-center justify-center gap-2 w-full font-normal" data-id="element-236">
            <Lock className="w-3.5 h-3.5" data-id="element-237" />
            Pago 100% seguro · Mercado Pago · Tarjeta · Transferencia
          </p>
        </motion.div>
      </div>
    </section>;
}